// SHARED FILE — kept in sync with luckynote_rn via check:search-highlight-core. Do not add React/DOM imports here.

const MIN_TERM_LENGTH = 2;
const MAX_TEXT_LENGTH = 20000;
const MAX_TERMS = 12;

export const escapeSearchRegex = value =>
    String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeSearchTerm = value => String(value || '').replace(/\s+/g, ' ').trim();

export const getSearchHighlightTerms = searchTerm => {
    const normalizedTerm = normalizeSearchTerm(searchTerm);
    if (normalizedTerm.length < MIN_TERM_LENGTH) {
        return [];
    }

    const terms = [];
    const seen = new Set();
    const addTerm = term => {
        const normalized = normalizeSearchTerm(term);
        const key = normalized.toLowerCase();
        if (normalized.length < MIN_TERM_LENGTH || seen.has(key)) {
            return;
        }
        seen.add(key);
        terms.push(normalized);
    };

    addTerm(normalizedTerm);
    const tokenMatches = normalizedTerm.match(/[A-Za-z0-9][A-Za-z0-9_'’-]*/g) || [];
    tokenMatches.forEach(addTerm);

    return terms
        .sort((a, b) => b.length - a.length)
        .slice(0, MAX_TERMS);
};

// A word regex used to tokenize both search terms and haystack text into
// word-boundary chunks for the stemming match strategy below.
const WORD_TOKEN_REGEX = /[A-Za-z0-9][A-Za-z0-9_'’-]*/g;

// A crude "Porter-lite" stemmer covering only the handful of common English
// inflection suffixes that show up constantly in notes (plurals, -ing verb
// forms). This is intentionally NOT a real stemmer/NLP library - it exists
// solely so the client's highlighter can find matches for the same simple
// word-form variants that MongoDB Atlas's lucene.english analyzer already
// matches server-side (e.g. "systems" query <-> "system" in the text).
//
// Known limitations (accepted, not fixed): irregular verbs ("ran"/"run"),
// e-restoration ("making" -> "mak" instead of "make"), and derivational
// suffixes ("management" vs "manage") are out of scope - those need a real
// stemmer/lemmatizer, which is explicitly not what this is.
export const simpleStem = word => {
    if (typeof word !== 'string') {
        return '';
    }

    const lower = word.toLowerCase();
    if (lower.length < 4) {
        return lower;
    }

    if (lower.endsWith('ing') && lower.length > 5) {
        let stem = lower.slice(0, -3);
        // Undo a doubled trailing consonant from the gerund form, e.g.
        // "running" -> "runn" -> "run". Leaves single-consonant stems (e.g.
        // "hoping" -> "hop") as-is - see e-restoration limitation above.
        const last = stem[stem.length - 1];
        const secondLast = stem[stem.length - 2];
        if (last && last === secondLast && !'aeiou'.includes(last)) {
            stem = stem.slice(0, -1);
        }
        return stem;
    }

    if (lower.endsWith('ies') && lower.length > 4) {
        return `${lower.slice(0, -3)}y`;
    }

    if (/(?:s|x|z|ch|sh)es$/.test(lower)) {
        return lower.slice(0, -2);
    }

    if (lower.endsWith('es') && lower.length > 4) {
        return lower.slice(0, -1);
    }

    if (lower.endsWith('s') && !lower.endsWith('ss')) {
        return lower.slice(0, -1);
    }

    return lower;
};

// Only plain, single-token English-looking words are eligible for stemming -
// URLs, numbers, short tokens, hyphenated/apostrophe'd words, and multi-word
// phrases fall back to exact/substring matching only, so stemming can't
// produce surprising matches on things it wasn't designed for.
export const isStemmableSearchTerm = term =>
    typeof term === 'string' && term.length >= 3 && /^[A-Za-z]+$/.test(term);

const mergeRanges = ranges => {
    if (!ranges.length) {
        return [];
    }

    const sorted = [...ranges].sort((a, b) => a.start - b.start || b.end - a.end);
    const merged = [sorted[0]];

    for (const range of sorted.slice(1)) {
        const previous = merged[merged.length - 1];
        if (range.start <= previous.end) {
            previous.end = Math.max(previous.end, range.end);
        } else {
            merged.push({ ...range });
        }
    }

    return merged;
};

export const findSearchHighlightRanges = (text, searchTerm) => {
    if (text != null && typeof text !== 'string' && typeof text !== 'number') {
        return [];
    }

    const source = String(text || '');
    if (!source) {
        return [];
    }

    const terms = getSearchHighlightTerms(searchTerm);
    if (!terms.length) {
        return [];
    }

    const searchableText = source.slice(0, MAX_TEXT_LENGTH);
    const ranges = [];

    // Lazily tokenize the haystack into words only if some term actually
    // needs the stemming match strategy - avoids the extra pass for the
    // common case (no stemmable terms / no stemmed matches needed).
    let textWordTokens = null;
    const getTextWordTokens = () => {
        if (textWordTokens === null) {
            textWordTokens = [];
            let wordMatch;
            WORD_TOKEN_REGEX.lastIndex = 0;
            while ((wordMatch = WORD_TOKEN_REGEX.exec(searchableText)) !== null) {
                textWordTokens.push({
                    start: wordMatch.index,
                    end: wordMatch.index + wordMatch[0].length,
                    stem: simpleStem(wordMatch[0])
                });
            }
        }
        return textWordTokens;
    };

    terms.forEach(term => {
        const pattern = new RegExp(escapeSearchRegex(term), 'gi');
        let match;
        while ((match = pattern.exec(searchableText)) !== null) {
            ranges.push({ start: match.index, end: match.index + match[0].length });
            if (match.index === pattern.lastIndex) {
                pattern.lastIndex += 1;
            }
        }

        // Additional stemming-aware match strategy (word-boundary only): if
        // the term looks like a plain single English word, also highlight
        // whole text-tokens whose own crude stem matches the term's stem
        // (e.g. query "systems" also matches "system" in the text). This is
        // additive to, never a replacement for, the exact/substring match
        // above.
        if (isStemmableSearchTerm(term)) {
            const termStem = simpleStem(term);
            getTextWordTokens().forEach(token => {
                const word = searchableText.slice(token.start, token.end);
                if (word.length < 3 || word.toLowerCase() === term.toLowerCase()) {
                    return;
                }
                if (token.stem === termStem) {
                    ranges.push({ start: token.start, end: token.end });
                }
            });
        }
    });

    return mergeRanges(ranges);
};

export const hasSearchHighlightMatch = (text, searchTerm) =>
    findSearchHighlightRanges(text, searchTerm).length > 0;

// Number of merged highlight ranges (i.e. distinct "hit" spans) in the text.
// Used for a "N matches" badge - independent of which terms produced them.
export const countSearchMatches = (text, searchTerm) =>
    findSearchHighlightRanges(text, searchTerm).length;

// Rough markdown-syntax stripper for building plain-text excerpts. Not a
// full markdown parser - just removes the common syntax markers (headings,
// emphasis, links, code fences/inline code, list markers, blockquotes) so a
// mid-body excerpt doesn't include stray "**"/"##"/"[]()" noise. Also strips
// a trailing "---\n[Timestamps]\n..." transcription appendix if present.
export const stripMarkdownForPlainText = value => {
    const source = String(value || '');
    if (!source) {
        return '';
    }

    // Drop a trailing horizontal-rule-delimited appendix (e.g. audio/video
    // transcription timestamps block) - it's not part of the note's prose.
    const withoutAppendix = source.replace(/\n\s*-{3,}\s*\n[\s\S]*$/, '');

    return withoutAppendix
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`([^`\n]+)`/g, '$1')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/^\s{0,3}#{1,6}\s+/gm, '')
        .replace(/^\s{0,3}>\s?/gm, '')
        .replace(/^\s{0,3}(?:[-*+]|\d+\.)\s+/gm, '')
        .replace(/^\s{0,3}[-*+]\s+\[(?: |x|X)\]\s+/gm, '')
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        .replace(/(\*|_)(.*?)\1/g, '$2')
        .replace(/~~(.*?)~~/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();
};

export const buildSearchMatchSnippet = (text, searchTerm, maxLength = 120, options = {}) => {
    if (text != null && typeof text !== 'string' && typeof text !== 'number') {
        return null;
    }

    const normalizedText = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalizedText || maxLength <= 0) {
        return null;
    }

    const firstRange = findSearchHighlightRanges(normalizedText, searchTerm)[0];
    if (!firstRange) {
        return null;
    }

    if (normalizedText.length <= maxLength) {
        return normalizedText;
    }

    const matchLength = Math.max(1, firstRange.end - firstRange.start);
    const preferredMatchOffset = Number.isFinite(options?.preferredMatchOffset)
        ? Math.max(0, Math.min(maxLength - matchLength, options.preferredMatchOffset))
        : null;
    const leftContextLength = preferredMatchOffset === null
        ? Math.floor(Math.max(0, maxLength - matchLength) / 2)
        : preferredMatchOffset;
    let start = Math.max(0, firstRange.start - leftContextLength);
    let end = Math.min(normalizedText.length, start + maxLength);

    if (preferredMatchOffset === null && end - start < maxLength) {
        start = Math.max(0, end - maxLength);
    }

    if (start === 0 && end - start < maxLength) {
        end = Math.min(normalizedText.length, maxLength);
    }

    const prefix = start > 0 ? '...' : '';
    const suffix = end < normalizedText.length ? '...' : '';

    return `${prefix}${normalizedText.slice(start, end).trim()}${suffix}`;
};

export const buildSearchMatchPreviewExcerpt = (
    text,
    searchTerm,
    {
        maxLength = 220,
        visibleWindow = 300
    } = {}
) => {
    const plainText = stripMarkdownForPlainText(text);
    if (!plainText) {
        return null;
    }

    const firstRange = findSearchHighlightRanges(plainText, searchTerm)[0];
    if (!firstRange || firstRange.start <= visibleWindow) {
        return null;
    }

    const snippet = buildSearchMatchSnippet(plainText, searchTerm, maxLength);
    if (!snippet) {
        return null;
    }

    const hasPrefix = snippet.startsWith('...');
    const hasSuffix = snippet.endsWith('...');

    return {
        text: snippet.slice(hasPrefix ? 3 : 0, hasSuffix ? snippet.length - 3 : snippet.length),
        hasPrefix,
        hasSuffix
    };
};
